import React, { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import { Modal, Button, Badge, LoadingSpinner } from './UI';

// Conciliar pela tela de Lançamentos (02/11/2026). Linha de fatura (PDF/Open
// Finance) → escolhe o lançamento do TechMiles; linha do TechMiles → escolhe a
// linha da fatura. O motor só SUGERE (com o TechMiles nada é automático): a
// sugestão aparece em cima, pra confirmar ou rejeitar, e os candidatos vêm
// ordenados pela mesma régua do auto-match. Quem grava é a Conciliação
// (POST /conciliacao/manual), com as travas de lá — mês finalizado, linha já
// coberta pelo par.
async function chamar(caminho, opcoes = {}) {
  const res = await fetch(`${API}${caminho}`, {
    ...opcoes, headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opcoes.headers || {}) },
  });
  const dados = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(dados.error || `Erro ${res.status}`);
  return dados;
}

const ORIGEM = { FATURA_PDF: 'PDF', FATURA: 'PDF', OPEN_FINANCE: 'Open Finance', XM_PRODUCAO: 'TechMiles', XMILES_PARC: 'TechMiles' };

export default function ConciliarLancamentoModal({ grupo, onClose, onFeito }) {
  const transacaoId = grupo.linha_banco_id || grupo.linha_operacao_id;
  const daFatura = !!grupo.linha_banco_id;
  const [texto, setTexto] = useState('');
  const [busca, setBusca] = useState('');
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setBusca(texto.trim()), 350);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    let vivo = true;
    setErro('');
    const qs = new URLSearchParams({ transacao_id: String(transacaoId) });
    if (busca) qs.set('q', busca);
    chamar(`/transacoes/visao/candidatos?${qs}`)
      .then(d => { if (vivo) setDados(d); })
      .catch(e => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [transacaoId, busca]);

  const executar = async (fn) => {
    setOcupado(true);
    setErro('');
    try { await fn(); onFeito(); } catch (e) { setErro(e.message); }
    setOcupado(false);
  };
  const conciliarCom = (c) => executar(() => chamar('/conciliacao/manual', {
    method: 'POST',
    body: JSON.stringify(daFatura
      ? { transacao_fatura_id: transacaoId, transacao_xmiles_id: c.id }
      : { transacao_fatura_id: c.id, transacao_xmiles_id: transacaoId }),
  }));
  const confirmar = (s) => executar(() => chamar(`/conciliacao/confirmar/${s.conciliacao_id}`, { method: 'POST' }));
  const rejeitar = (s) => executar(() => chamar(`/conciliacao/rejeitar/${s.conciliacao_id}`, { method: 'POST' }));

  const mudo = { fontSize: 12, color: colors.textMuted };
  const caixa = { padding: 12, borderRadius: 8, background: colors.bg, border: `1px solid ${colors.border}` };
  const base = dados && dados.base;
  // O lançamento da sugestão já está na caixa de cima (Confirmar/Rejeitar):
  // conciliá-lo de novo pela lista deixaria a sugestão antiga sobrando.
  const jaSugeridos = new Set((grupo.sugestoes || []).map(s => (daFatura ? s.techmiles_id : s.fatura_id)));
  const candidatos = dados ? dados.candidatos.filter(c => !jaSugeridos.has(c.id)) : [];

  return (
    <Modal isOpen onClose={onClose} title={daFatura ? 'Conciliar com o TechMiles' : 'Conciliar com a fatura'} width={820}>
      <div style={{ ...caixa, marginBottom: 12 }}>
        <div style={mudo}>{daFatura ? 'Linha da fatura' : 'Lançamento do TechMiles'}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 4 }}>
          <span style={{ color: colors.text, fontWeight: 600 }}>{grupo.descricao}</span>
          <span style={{ color: colors.text, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(grupo.valor)}</span>
        </div>
        <div style={mudo}>{grupo.data} · {grupo.nome_cartao}{grupo.parcela ? ` · parcela ${grupo.parcela}` : ''}</div>
      </div>

      {grupo.sugestoes && grupo.sugestoes.length > 0 && (
        <div style={{ ...caixa, marginBottom: 12, borderColor: colors.warning }}>
          <div style={{ fontSize: 13, color: colors.text, marginBottom: 8 }}>Sugestão do motor de conciliação</div>
          {grupo.sugestoes.map(s => (
            <div key={s.conciliacao_id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ flex: 1, color: colors.text, fontSize: 13 }}>
                {daFatura ? s.techmiles_descricao : s.fatura_descricao}
                <span style={{ ...mudo, marginLeft: 8 }}>{fmt(daFatura ? s.techmiles_valor : s.fatura_valor)}</span>
              </span>
              {s.confianca != null && <Badge text={`${s.confianca}%`} color={colors.warning} />}
              <Button size="sm" icon={Check} disabled={ocupado} onClick={() => confirmar(s)}>Confirmar</Button>
              <Button size="sm" variant="outline" color={colors.danger} icon={X} disabled={ocupado} onClick={() => rejeitar(s)}>Rejeitar</Button>
            </div>
          ))}
        </div>
      )}

      <input value={texto} onChange={e => setTexto(e.target.value)}
        placeholder={daFatura ? 'Buscar no TechMiles do titular (descrição, fidelizado, valor)…' : 'Buscar na fatura do titular (descrição, valor)…'}
        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 8, marginBottom: 8,
          border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 13 }} />
      <div style={{ ...mudo, marginBottom: 6 }}>
        {busca ? 'Resultado da busca no titular (90 dias)' : 'Candidatos: mesmo cartão, valor até 10% e data até 20 dias'}
      </div>

      {erro && <div style={{ ...caixa, borderColor: colors.danger, color: colors.danger, fontSize: 13, marginBottom: 8 }}>{erro}</div>}
      {!dados && !erro && <LoadingSpinner />}
      {dados && (
        <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: 8 }}>
          {candidatos.length === 0 && (
            <div style={{ padding: 14, ...mudo, textAlign: 'center' }}>
              Nenhum candidato. Tente a busca — por descrição, fidelizado ou valor.
            </div>
          )}
          {candidatos.map(c => {
            const diff = base ? Math.round((c.valor - base.valor) * 100) / 100 : 0;
            return (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: colors.text, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.descricao}
                  </div>
                  <div style={mudo}>
                    {c.data} · {ORIGEM[c.origem] || c.origem}{c.tem_par ? ' (com par no Open Finance)' : ''} · {c.nome_cartao}
                    {c.fidelizado ? ` · 👤 ${c.fidelizado}` : ''}{c.parcela ? ` · ${c.parcela}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ color: colors.text, fontWeight: 700, fontSize: 13 }}>{fmt(c.valor)}</div>
                  {diff !== 0 && <div style={{ fontSize: 11, color: colors.warning }}>dif. {fmt(diff)}</div>}
                </div>
                <Badge text={`${c.confianca}%`} color={c.confianca >= 70 ? colors.success : c.confianca >= 40 ? colors.warning : colors.textMuted} />
                <Button size="sm" disabled={ocupado} onClick={() => conciliarCom(c)}>Conciliar</Button>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
